import React, { useEffect, useRef } from 'react';
import { Search, UserCheck, Code, Rocket, ArrowRight } from 'lucide-react';

const steps = [
  {
    icon: Search,
    number: '01',
    title: 'Discover',
    description: 'Deep-dive into your vision, stack, and constraints. The output: a clear scope document, architecture blueprint, and fixed-fee estimate.',
    detail: '1–2 weeks',
    highlights: ['Requirements audit', 'Architecture design', 'Fixed-fee proposal'],
  },
  {
    icon: UserCheck,
    number: '02',
    title: 'Assemble',
    description: 'Hand-picked cross-functional team — AI, frontend, backend, data — matched to your exact needs. Not generalists; specialists.',
    detail: '48 hours',
    highlights: ['Role-matched experts', 'Domain specialists', 'Seamless onboarding'],
  },
  {
    icon: Code,
    number: '03',
    title: 'Build',
    description: 'Iterative sprints with full transparency. Production-ready code, comprehensive testing, and weekly progress demos.',
    detail: '2-week sprints',
    highlights: ['Weekly demos', 'CI/CD pipeline', 'Code reviews'],
  },
  {
    icon: Rocket,
    number: '04',
    title: 'Scale',
    description: 'From launch to growth — we optimize, monitor, and scale your product. Full knowledge transfer and SRE support included.',
    detail: 'Ongoing',
    highlights: ['Production deploy', 'Knowledge transfer', 'SRE support'],
  },
];

const ProcessSection = () => {
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    refs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* Ambient glows */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.04) 0%, transparent 70%)' }} />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.03) 0%, transparent 70%)' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div ref={(el) => (refs.current[0] = el)} className="reveal text-center mb-16 sm:mb-20">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 font-heading" style={{ color: '#2693FF' }}>
            How We Work
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading mb-5">
            From idea to production
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            A battle-tested methodology honed over 100+ engagements. Every project
            follows this process — no shortcuts, no surprises.
          </p>
        </div>

        {/* Desktop: 4-column cards with connecting timeline */}
        <div className="hidden lg:block relative">
          {/* Connecting line through step circles */}
          <div className="absolute top-[40px] left-[calc(12.5%)] right-[calc(12.5%)] h-[2px] z-0">
            <div
              className="w-full h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, rgba(38,147,255,0.1) 0%, rgba(38,147,255,0.3) 30%, rgba(38,147,255,0.3) 70%, rgba(38,147,255,0.1) 100%)' }}
            />
          </div>

          <div className="grid grid-cols-4 gap-6 relative z-10">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  ref={(el) => (refs.current[index + 1] = el)}
                  className="reveal flex flex-col items-center h-full"
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  {/* Step circle on the timeline */}
                  <div className="relative mb-8">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center bg-white border-2 transition-all duration-300"
                      style={{
                        borderColor: '#2693FF',
                        boxShadow: '0 0 0 6px rgba(38,147,255,0.08), 0 4px 16px rgba(38,147,255,0.12)',
                      }}
                    >
                      <Icon className="w-8 h-8" style={{ color: '#2693FF' }} />
                    </div>
                    {/* Step number */}
                    <span
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center text-white"
                      style={{ background: '#2693FF' }}
                    >
                      {step.number}
                    </span>
                  </div>

                  {/* Card — flex-1 ensures equal height across columns */}
                  <div
                    className="card-lift bg-white rounded-2xl p-6 border border-slate-100 text-center w-full flex-1 flex flex-col"
                    style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                  >
                    <h3 className="text-lg font-bold text-slate-900 font-heading mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4 flex-1">
                      {step.description}
                    </p>

                    {/* Highlight pills */}
                    <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                      {step.highlights.map((h) => (
                        <span
                          key={h}
                          className="text-[11px] px-2.5 py-1 rounded-full font-medium border border-slate-150 text-slate-500"
                          style={{ background: '#F8FAFC' }}
                        >
                          {h}
                        </span>
                      ))}
                    </div>

                    {/* Timeline detail */}
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#2693FF' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#2693FF' }} />
                      {step.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile/Tablet: Stacked cards */}
        <div className="lg:hidden relative">
          <div className="space-y-6">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.number}>
                  <div
                    className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-100"
                    style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                  >
                    {/* Top row: icon + title + step number */}
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                        style={{
                          borderColor: '#2693FF',
                          boxShadow: '0 0 0 4px rgba(38,147,255,0.08)',
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: '#2693FF' }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900 font-heading">
                          {step.title}
                        </h3>
                      </div>
                      <span
                        className="w-8 h-8 rounded-full text-xs font-bold flex items-center justify-center text-white flex-shrink-0"
                        style={{ background: '#2693FF' }}
                      >
                        {step.number}
                      </span>
                    </div>

                    <p className="text-sm text-slate-500 leading-relaxed mb-3">
                      {step.description}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {step.highlights.map((h) => (
                        <span
                          key={h}
                          className="text-[11px] px-2.5 py-1 rounded-full font-medium border border-slate-150 text-slate-500"
                          style={{ background: '#F8FAFC' }}
                        >
                          {h}
                        </span>
                      ))}
                    </div>

                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#2693FF' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#2693FF' }} />
                      {step.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div ref={(el) => (refs.current[5] = el)} className="reveal text-center mt-16 sm:mt-20">
          <a
            href="#contact"
            className="group inline-flex items-center gap-2 text-sm font-semibold transition-colors"
            style={{ color: '#2693FF' }}
          >
            Start your project
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
